import { useMyOrderIntentionsContext } from 'contexts/MyOrderIntentionsContext';
import { OrderIntention } from 'model/OrderIntention';
import { Key, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { myOrderIntentionEditSelectors } from 'store/state/selectors';
import { myOrderIntentionEditActions } from 'store/state/slices';
import {
  hasMixedStatusOrOrigin,
  isSelectionBlocked,
} from './orderIntentionRules';

type StatusAndOrigin = { status: string; origin: string };

export function useOrderIntentionSelection(data: OrderIntention[] | undefined) {
  const dispatch = useDispatch();
  const clearCheckbox = useSelector(
    myOrderIntentionEditSelectors.getClearCheckbox,
  );
  const {
    selectedOrderIntentions,
    setSelectedOrderIntentions,
    selectedOrderIntentionsKeys,
    setSelectedOrderIntentionsKeys,
  } = useMyOrderIntentionsContext();
  const [selectedStatusAndOrigin, setSelectedStatusAndOrigin] =
    useState<StatusAndOrigin>();

  const onChangeSelecteds = useCallback(
    (selectedRowKeys: Key[], selectedOrders: OrderIntention[]) => {
      setSelectedOrderIntentionsKeys(selectedRowKeys);
      setSelectedOrderIntentions(selectedOrders);
      if (selectedOrders.length) {
        setSelectedStatusAndOrigin({
          status: selectedOrders[0].status,
          origin: selectedOrders[0].origin,
        });
        return;
      }
      setSelectedStatusAndOrigin(undefined);
    },
    [setSelectedOrderIntentions, setSelectedOrderIntentionsKeys],
  );

  const disableCheckbox = useCallback(
    (orderSelected: OrderIntention) => {
      if (
        selectedStatusAndOrigin &&
        (orderSelected.status !== selectedStatusAndOrigin.status ||
          orderSelected.origin !== selectedStatusAndOrigin.origin)
      ) {
        return true;
      }
      return isSelectionBlocked(orderSelected.status, orderSelected.origin);
    },
    [selectedStatusAndOrigin],
  );

  const getCheckboxProps = useCallback(
    (orderSelected: OrderIntention) => ({
      disabled: disableCheckbox(orderSelected),
    }),
    [disableCheckbox],
  );

  const isSelected = useCallback(
    (order: OrderIntention) => selectedOrderIntentionsKeys.includes(order.id),
    [selectedOrderIntentionsKeys],
  );

  const toggleOrder = useCallback(
    (order: OrderIntention) => {
      const remaining = selectedOrderIntentions.filter(
        (selected) => selected.id !== order.id,
      );
      const next = isSelected(order)
        ? remaining
        : [...selectedOrderIntentions, order];
      onChangeSelecteds(
        next.map((selected) => selected.id),
        next,
      );
    },
    [isSelected, onChangeSelecteds, selectedOrderIntentions],
  );

  const selectableOrders = (data ?? []).filter(
    (order) => !isSelectionBlocked(order.status, order.origin),
  );

  const toggleAll = useCallback(() => {
    const next =
      selectedOrderIntentions.length === selectableOrders.length
        ? []
        : selectableOrders;
    onChangeSelecteds(
      next.map((selected) => selected.id),
      next,
    );
  }, [onChangeSelecteds, selectableOrders, selectedOrderIntentions.length]);

  useEffect(() => {
    if (clearCheckbox) {
      setSelectedOrderIntentionsKeys([]);
      setSelectedOrderIntentions([]);
      setSelectedStatusAndOrigin(undefined);
      dispatch(myOrderIntentionEditActions.setClearCheckbox(false));
    }
  }, [clearCheckbox]);

  return {
    selectedOrderIntentions,
    selectedOrderIntentionsKeys,
    onChangeSelecteds,
    getCheckboxProps,
    disableCheckbox,
    isSelected,
    toggleOrder,
    toggleAll,
    selectableCount: selectableOrders.length,
    hideSelectAll: hasMixedStatusOrOrigin(data),
  };
}

export type OrderIntentionSelection = ReturnType<
  typeof useOrderIntentionSelection
>;
